import React, {useState, useEffect} from 'react';
import {useParams, useLocation} from "react-router-dom";
import {useDispatch, useSelector} from 'react-redux';

import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage, dataTaggerActionClear, dataTaggerActionSet } from '@laramie/ui';
import { useForm, useManagedContext, useNav, useUnsavedChangesGuard, useFormErrors } from '../../components/hooks';
import {APIS} from '../../config/apis';
import {ServerRequest} from '../../utils/apiweb';

import {Loading, InputNumber, DatePickerCustom, SectionHeading, InputTasa, Accordion, CheckboxButton, CheckboxButtonGroup, FormError, FormLabel} from '../../components/common';
import {getDateId} from '../../utils/convert';
import {CloneObject} from '../../utils/helpers';
import SubTasaImputacionesGrid from '../../components/controls/SubTasaImputacionesGrid';
import SubTasaValoresGrid from '../../components/controls/SubTasaValoresGrid';
import DataTaggerFormRedux from '../../components/controls/DataTaggerFormRedux';
import {useMemo} from 'react';
import {MESSAGE_MODALS} from '../../context/custom/MessageModalContext';
import {VALIDATION_MESSAGE} from '../../consts/validationMessage';

function SubTasaView() {

    //parametros url
    const params = useParams();
    const location = useLocation();
    const navigate = useNav()
    const {showMessageModal} = useManagedContext()
    const previousFilters = location.state?.filters;

    //variables
    const entityInit = {
        subTasa: {
            id: 0,
            idTasa: 0,
            descTasa: '',
            descTipoTributo: '',
            descCategoriaTasa: '',
            codigo: '',
            descripcion: '',
            impuestoNacional: 0,
            impuestoProvincial: 0,
            ctasCtes: 0,
            timbradosExtras: 0,
            descripcionReducida: '',
            fechaDesde: null,
            fechaHasta: null,
            rubroGenerico: false,
            liquidableCtaCte: false,
            liquidableDDJJ: false,
            actualizacion: false,
            accesorios: false,
            internetDDJJ: false,
            imputXPorc: false
        },
        archivos: [],
        observaciones: [],
        etiquetas: [],
        subTasaImputaciones: [],
        subTasaValores: []
    };

    // Hooks

    const [state, setState] = useState({
        processKey: `SubTasa_${params.id ?? 0}_${getDateId()}`,
        id: params.id ? parseInt(params.id) : 0,
        mode: params.mode,
        loading: false,
        entity: entityInit,
    });
    const [pendingChange, setPendingChange] = useState(false);
    const dispatch = useDispatch();
    const dataTagger = useSelector((state) => state.dataTagger.data);

    const {allowNavigation} = useUnsavedChangesGuard(pendingChange);

    const mount = () => {
        if (state.id > 0) {
            FindSubTasa();
        } else {
            dispatch(dataTaggerActionSet(state.processKey, {
                Archivo: [],
                Observacion: [],
                Etiqueta: []
            }));
        }

        const unmount = () => {
        }
        return unmount;
    }
    useEffect(mount, []);

    const [formValues, formHandle, , formSet] = useForm({
        idTasa: 0,
        descTasa: '',
        codigo: '',
        descripcion: '',
        impuestoNacional: 0,
        impuestoProvincial: 0,
        ctasCtes: 0,
        timbradosExtras: 0,
        descripcionReducida: '',
        fechaDesde: null,
        fechaHasta: null,
        rubroGenerico: false,
        liquidableCtaCte: false,
        liquidableDDJJ: false,
        actualizacion: false,
        accesorios: false,
        internetDDJJ: false,
        imputXPorc: false
    });

    const isNoHeader = useMemo(() => window.location.pathname.split('/')[2] === 'nh', [window.location.pathname])

    const [subTasasOpen, setSubTasasOpen] = useState(true);

    const { errorOf, validate } = useFormErrors(getErrors);

    // Handlers
    const formHandleProxy = (event) => {
        formHandle(event);
        setPendingChange(true);
    }
    const handleClickGuardar = () => {
        if (Object.keys(getErrors()).length > 0) setSubTasasOpen(true);

        if (validate()) {
            if (state.id === 0) {
                AddSubTasa();
            } else {
                ModifySubTasa();
            }
        }
        ;
    }

    const handleClickSalir = () => {
        if (!pendingChange) {
            dispatch(dataTaggerActionClear(state.processKey));
            window.close()
        } else showMessageModal({
            ...MESSAGE_MODALS.CONFIRM_SALIR, onConfirm: () => {
                allowNavigation();
                window.close();
            }
        })
    }

    const handleClickVolver = () => {
        if (!pendingChange) dispatch(dataTaggerActionClear(state.processKey));

        const navOptions = {to: `/sub-tasas`};
        if (previousFilters) {
            navOptions.options = {state: {filters: previousFilters}};
        }

        navigate(navOptions);
    }

    // Callbacks

    const callbackNoSuccess = (response) => {
        response.json()
            .then((error) => {
                const message = error.message;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
    }
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
            return {...prevState, loading: false};
        });
    }
    const callbackSuccessFindSubTasa = (response) => {
        response.json()
            .then((data) => {

                //deserialize date fields
                if (data.subTasa.fechaDesde) data.subTasa.fechaDesde = new Date(data.subTasa.fechaDesde);
                if (data.subTasa.fechaHasta) data.subTasa.fechaHasta = new Date(data.subTasa.fechaHasta);

                data.subTasaValores.forEach(x => {
                    if (x.fechaDesde) x.fechaDesde = new Date(x.fechaDesde);
                    if (x.fechaHasta) x.fechaHasta = new Date(x.fechaHasta);
                });
                data.archivos.forEach(x => {
                    if (x.fecha) x.fecha = new Date(x.fecha);
                });
                data.observaciones.forEach(x => {
                    if (x.fecha) x.fecha = new Date(x.fecha);
                });

                setState(prevState => {
                    return {...prevState, loading: false, entity: data};
                });
                setPendingChange(false);
                formSet({
                    idTasa: data.subTasa.idTasa,
                    codigo: data.subTasa.codigo,
                    descripcion: data.subTasa.descripcion,
                    impuestoNacional: data.subTasa.impuestoNacional,
                    impuestoProvincial: data.subTasa.impuestoProvincial,
                    ctasCtes: data.subTasa.ctasCtes,
                    timbradosExtras: data.subTasa.timbradosExtras,
                    descripcionReducida: data.subTasa.descripcionReducida,
                    fechaDesde: data.subTasa.fechaDesde,
                    fechaHasta: data.subTasa.fechaHasta,
                    rubroGenerico: data.subTasa.rubroGenerico,
                    liquidableCtaCte: data.subTasa.liquidableCtaCte,
                    liquidableDDJJ: data.subTasa.liquidableDDJJ,
                    actualizacion: data.subTasa.actualizacion,
                    accesorios: data.subTasa.accesorios,
                    internetDDJJ: data.subTasa.internetDDJJ,
                    imputXPorc: data.subTasa.imputXPorc
                });
                dispatch(dataTaggerActionSet(state.processKey, {
                    Archivo: data.archivos,
                    Observacion: data.observaciones,
                    Etiqueta: data.etiquetas
                }));
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
    }

    // Functions

    function getErrors() {
        const errors = {};

        if (formValues.codigo.length === 0) errors.codigo = VALIDATION_MESSAGE.REQUIRED;
        if (formValues.descripcion.length === 0) errors.descripcion = VALIDATION_MESSAGE.REQUIRED;

        return errors;
    }

    function FindSubTasa() {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const paramsUrl = `/${state.id}`;

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.SUB_TASA,
            paramsUrl,
            null,
            callbackSuccessFindSubTasa,
            callbackNoSuccess,
            callbackError
        );

    }

    function AddSubTasa() {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            response.json()
                .then((row) => {
                    ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, "Sub tasa ingresada correctamente", () => {
                        dispatch(dataTaggerActionClear(state.processKey));
                        setState(prevState => {
                            return {...prevState, loading: false};
                        });
                        allowNavigation();
                        navigate({
                            to: `/sub-tasa/edit/${row.subTasa.id}`,
                            refresh: true,
                            replace: true,
                        })
                    });
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => {
                        return {...prevState, loading: false};
                    });
                });
        };

        const dataBody = {
            subTasa: {
                ...state.entity.subTasa,
                idTasa: parseInt(formValues.idTasa),
                codigo: formValues.codigo,
                descripcion: formValues.descripcion,
                impuestoNacional: formValues.impuestoNacional,
                impuestoProvincial: formValues.impuestoProvincial,
                ctasCtes: formValues.ctasCtes,
                timbradosExtras: formValues.timbradosExtras,
                descripcionReducida: formValues.descripcionReducida,
                fechaDesde: formValues.fechaDesde,
                fechaHasta: formValues.fechaHasta,
                rubroGenerico: formValues.rubroGenerico,
                liquidableCtaCte: formValues.liquidableCtaCte,
                liquidableDDJJ: formValues.liquidableDDJJ,
                actualizacion: formValues.actualizacion,
                accesorios: formValues.accesorios,
                internetDDJJ: formValues.internetDDJJ,
                imputXPorc: formValues.imputXPorc
            },
            archivos: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Archivo.filter(f => f.state !== 'o') : [],
            observaciones: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Observacion.filter(f => f.state !== 'o') : [],
            etiquetas: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Etiqueta.filter(f => f.state !== 'o') : [],
            subTasaImputaciones: [],
            subTasaValores: []
        };

        ServerRequest(
            REQUEST_METHOD.POST,
            null,
            true,
            APIS.URLS.SUB_TASA,
            null,
            dataBody,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );

    }

    function ModifySubTasa() {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, "Sub tasa actualizada correctamente", () => {
                dispatch(dataTaggerActionClear(state.processKey));
                callbackSuccessFindSubTasa(response);
            });
        };

        const dataBody = {
            subTasa: {
                ...state.entity.subTasa,
                idTasa: parseInt(formValues.idTasa),
                codigo: formValues.codigo,
                descripcion: formValues.descripcion,
                impuestoNacional: formValues.impuestoNacional,
                impuestoProvincial: formValues.impuestoProvincial,
                ctasCtes: formValues.ctasCtes,
                timbradosExtras: formValues.timbradosExtras,
                descripcionReducida: formValues.descripcionReducida,
                fechaDesde: formValues.fechaDesde,
                fechaHasta: formValues.fechaHasta,
                rubroGenerico: formValues.rubroGenerico,
                liquidableCtaCte: formValues.liquidableCtaCte,
                liquidableDDJJ: formValues.liquidableDDJJ,
                actualizacion: formValues.actualizacion,
                accesorios: formValues.accesorios,
                internetDDJJ: formValues.internetDDJJ,
                imputXPorc: formValues.imputXPorc
            },
            archivos: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Archivo.filter(f => f.state !== 'o') : [],
            observaciones: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Observacion.filter(f => f.state !== 'o') : [],
            etiquetas: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Etiqueta.filter(f => f.state !== 'o') : [],
            subTasaImputaciones: state.entity.subTasaImputaciones, // Borro el filtro por 'o' para validar por ejercicio
            subTasaValores: state.entity.subTasaValores.filter(f => f.state !== 'o'),
        };
        const paramsUrl = `/${state.id}`;

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.SUB_TASA,
            paramsUrl,
            dataBody,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );

    }

    function UpdateEntity(typeEntity, row) {
        // o:original a:add m:modify r:remove c:modify only childs

        let entity = CloneObject(state.entity);

        if (typeEntity === 'SubTasaImputacion') {
            if (row.state === 'a') {
                entity.subTasaImputaciones.push(row);
            } else if (row.state === 'm') {
                const index = entity.subTasaImputaciones.indexOf(entity.subTasaImputaciones.find(x => x.id === row.id));
                if (index !== -1) {
                    if (row.id < 0) row.state = 'a'; //originalmente era un alta
                    entity.subTasaImputaciones[index] = row;
                }
            } else if (row.state === 'r') {
                if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                    entity.subTasaImputaciones = entity.subTasaImputaciones.filter(f => f.id !== row.id);
                } else {
                    let item = entity.subTasaImputaciones.find(x => x.id === row.id);
                    item.state = 'r';
                }
            } else if (row.state === 'r') {
                if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                    entity.subTasaValores = entity.subTasaValores.filter(f => f.id !== row.id);
                } else {
                    let item = entity.subTasaValores.find(x => x.id === row.id);
                    item.state = 'r';
                }
            }
        }

        if (typeEntity === 'SubTasaValor') {
            if (row.state === 'a') {
                entity.subTasaValores.push(row);
            } else if (row.state === 'm') {
                const index = entity.subTasaValores.indexOf(entity.subTasaValores.find(x => x.id === row.id));
                if (index !== -1) {
                    if (row.id < 0) row.state = 'a'; //originalmente era un alta
                    entity.subTasaValores[index] = row;
                }
            } else if (row.state === 'r') {
                if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                    entity.subTasaValores = entity.subTasaValores.filter(f => f.id !== row.id);
                } else {
                    let item = entity.subTasaValores.find(x => x.id === row.id);
                    item.state = 'r';
                }
            } else if (row.state === 'r') {
                if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                    entity.subTasaValores = entity.subTasaValores.filter(f => f.id !== row.id);
                } else {
                    let item = entity.subTasaValores.find(x => x.id === row.id);
                    item.state = 'r';
                }
            }
        }

        setState(prevState => {
            return {...prevState, entity: entity};
        });
        setPendingChange(true);
    }


    return (
        <>

            <Loading visible={state.loading}></Loading>

            <SectionHeading
                titles={[
                    {title: 'Entidades'},
                    {title: 'Sub tasas', url: '/sub-tasas'},
                    {title: state.id === 0 ? 'Nueva' : `Código: ${state.entity.subTasa.codigo}`},
                ]}
            />

            <div className="m-top-20">

                <section className='section-accordion'>
                    <div className="m-top-20 m-bottom-20">
                        <Accordion title="Sub tasas" open={subTasasOpen} onToggle={setSubTasasOpen}>
                            <div className='row form-basic'>
                                <div className="col-12 col-md-4">
                                    <label htmlFor="idTasa" className="form-label">Tasa</label>
                                    <InputTasa
                                        name="idTasa"
                                        placeholder=""
                                        className="form-control"
                                        value={formValues.idTasa}
                                        disabled={state.mode !== OPERATION_MODE.NEW} // Unicamente se puede modificar cuando se crea la subtasa.
                                        onChange={({target}) => {
                                            let idTasa = 0;
                                            let descTasa = '';
                                            if (target.row) {
                                                idTasa = parseInt(target.value);
                                                descTasa = target.row.descripcion;
                                            }
                                            formSet({...formValues, idTasa: idTasa, descTasa: descTasa});
                                        }}
                                    />
                                    <FormError />
                                </div>
                                <div className="col-12 col-md-4 col-lg-2">
                                    <FormLabel htmlFor="codigo" required>Sub tasa</FormLabel>
                                    <input
                                        name="codigo"
                                        type="text"
                                        placeholder=""
                                        className={`form-control ${errorOf('codigo') ? 'is-invalid' : ''}`}
                                        value={formValues.codigo}
                                        onChange={formHandleProxy}
                                        disabled={state.mode === OPERATION_MODE.VIEW}
                                    />
                                    <FormError message={errorOf('codigo')} />
                                </div>
                                <div className="col-12 col-md-8 col-lg-6">
                                    <FormLabel htmlFor="descripcion" required>Descripción</FormLabel>
                                    <input
                                        name="descripcion"
                                        type="text"
                                        placeholder=""
                                        className={`form-control ${errorOf('descripcion') ? 'is-invalid' : ''}`}
                                        value={formValues.descripcion}
                                        onChange={formHandleProxy}
                                        disabled={state.mode === OPERATION_MODE.VIEW}
                                    />
                                    <FormError message={errorOf('descripcion')} />
                                </div>
                                {/* {<div className="col-12 col-md-2">
                                <label htmlFor="impuestoNacional" className="form-label">Imp. Nacional</label>
                                <InputNumber
                                    name="impuestoNacional"
                                    placeholder=""
                                    className="form-control"
                                    value={ formValues.impuestoNacional }
                                    onChange={ formHandleProxy }
                                    precision={2}
                                    disabled={ state.mode === OPERATION_MODE.VIEW }
                                />
                            </div>                                
                            <div className="col-6 col-md-2">
                                <label htmlFor="impuestoProvincial" className="form-label">Imp. Provincial</label>
                                <InputNumber
                                    name="impuestoProvincial"
                                    placeholder=""
                                    className="form-control"
                                    value={ formValues.impuestoProvincial }
                                    onChange={ formHandleProxy }
                                    precision={2}
                                    disabled={ state.mode === OPERATION_MODE.VIEW }
                                />
                            </div>
                            <div className="col-6 col-md-2">
                                <label htmlFor="ctasCtes" className="form-label">Ctas. Ctes.</label>
                                <InputNumber
                                    name="ctasCtes"
                                    placeholder=""
                                    className="form-control"
                                    value={ formValues.ctasCtes }
                                    onChange={ formHandleProxy }
                                    precision={2}
                                    disabled={state.mode === OPERATION_MODE.VIEW}
                                />
                            </div>
                            <div className="col-12 col-md-2">
                                <label htmlFor="timbradosExtras" className="form-label">Timbrados extras</label>
                                <InputNumber
                                    name="timbradosExtras"
                                    placeholder=""
                                    className="form-control"
                                    value={ formValues.timbradosExtras }
                                    onChange={ formHandleProxy }
                                    precision={2}
                                    disabled={state.mode === OPERATION_MODE.VIEW}
                                />
                            </div>} */}
                                <div className="col-12 col-md-4">
                                    <label htmlFor="descripcionReducida" className="form-label">Descripción
                                        reducida</label>
                                    <input
                                        name="descripcionReducida"
                                        type="text"
                                        placeholder=""
                                        className="form-control"
                                        value={formValues.descripcionReducida}
                                        onChange={formHandleProxy}
                                        disabled={state.mode === OPERATION_MODE.VIEW}
                                    />
                                </div>
                                <div className="col-6 col-md-4 col-lg-2">
                                    <label htmlFor="fechaDesde" className="form-label">Fecha desde</label>
                                    <DatePickerCustom
                                        name="fechaDesde"
                                        placeholder=""
                                        className="form-control"
                                        value={formValues.fechaDesde}
                                        onChange={formHandleProxy}
                                        disabled={state.mode === OPERATION_MODE.VIEW}
                                    />
                                </div>
                                <div className="col-6 col-md-4 col-lg-2">
                                    <label htmlFor="fechaHasta" className="form-label">Fecha hasta</label>
                                    <DatePickerCustom
                                        name="fechaHasta"
                                        placeholder=""
                                        className="form-control"
                                        value={formValues.fechaHasta}
                                        onChange={formHandleProxy}
                                        disabled={state.mode === OPERATION_MODE.VIEW}
                                        minValue={formValues.fechaDesde}
                                    />
                                </div>
                                <div className="col-12 col-md-auto">
                                    <CheckboxButtonGroup label="Comportamiento">
                                        <CheckboxButton
                                            name="actualizacion"
                                            label="Se actualiza"
                                            checked={formValues.actualizacion}
                                            onChange={formHandleProxy}
                                            disabled={state.mode === OPERATION_MODE.VIEW}
                                        />
                                        <CheckboxButton
                                            name="liquidableDDJJ"
                                            label="Se liquida por DDJJ"
                                            checked={formValues.liquidableDDJJ}
                                            onChange={formHandleProxy}
                                            disabled={state.mode === OPERATION_MODE.VIEW}
                                        />
                                    </CheckboxButtonGroup>
                                </div>
                            </div>
                        </Accordion>

                        {state.mode !== OPERATION_MODE.NEW &&
                            <>
                                <div className="m-top-20">
                                    <Accordion title="Imputaciones" startOpen={false}>
                                        <SubTasaImputacionesGrid
                                            processKey={state.processKey}
                                            disabled={state.mode !== OPERATION_MODE.EDIT}
                                            data={{
                                                idTasa: formValues.idTasa,
                                                idSubTasa: state.id,
                                                list: state.entity.subTasaImputaciones
                                            }}
                                            onChange={(typeEntity, row) => UpdateEntity(typeEntity, row)}
                                        />
                                    </Accordion>
                                    <div className="m-top-20">
                                        <Accordion title="Valores" startOpen={false}>
                                            <SubTasaValoresGrid
                                                processKey={state.processKey}
                                                disabled={state.mode !== OPERATION_MODE.EDIT}
                                                data={{
                                                    idTasa: formValues.idTasa,
                                                    idSubTasa: state.id,
                                                    list: state.entity.subTasaValores
                                                }}
                                                onChange={(typeEntity, row) => UpdateEntity(typeEntity, row)}
                                            />
                                        </Accordion>
                                    </div>
                                </div>
                            </>
                        }

                        <div className="m-top-20">
                            <Accordion title="Información adicional" startOpen={false}>
                                <DataTaggerFormRedux
                                    title="Información adicional de la Sub tasa"
                                    processKey={state.processKey}
                                    entidad="SubTasa"
                                    idEntidad={state.id}
                                    disabled={state.mode !== OPERATION_MODE.EDIT}
                                    onChange={(row) => setPendingChange(true)}
                                />
                            </Accordion>
                        </div>
                    </div>
                </section>
            </div>


            <footer className='footer footer-action'>
                <div className='footer-action-container'>
                    {isNoHeader ? (
                        <button className="btn back-button float-start" onClick={handleClickSalir}>Volver</button>
                    ) : (
                        <button className="btn back-button float-start"
                                onClick={(event) => handleClickVolver()}>Volver</button>
                    )}
                    {state.mode !== OPERATION_MODE.VIEW &&
                        <button
                            className={pendingChange ? "btn action-button float-end" : "btn action-button btn-disabled float-end"}
                            onClick={(event) => handleClickGuardar()}
                            disabled={!pendingChange}
                        >
                            Guardar
                        </button>
                    }
                </div>
            </footer>

        </>
    )
}

export default SubTasaView;
